'use client';

import type { ButtonProps, TextProps } from '@chakra-ui/react';
import {
  Button,
  Pagination as ChakraPagination,
  HStack,
  IconButton,
  Text,
  createContext,
  useBreakpointValue,
  usePaginationContext,
} from '@chakra-ui/react';
import * as React from 'react';
import { LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight, LuEllipsis } from 'react-icons/lu';

import { LinkButton } from './link-button';

interface ButtonVariantMap {
  current: ButtonProps['variant'];
  default: ButtonProps['variant'];
  ellipsis: ButtonProps['variant'];
}

type PaginationVariant = 'outline' | 'solid' | 'subtle';

interface ButtonVariantContext {
  size: ButtonProps['size'];
  variantMap: ButtonVariantMap;
  getHref?: (page: number) => string;
}

const [RootPropsProvider, useRootProps] = createContext<ButtonVariantContext>({
  name: 'RootPropsProvider',
});

export interface PaginationRootProps extends Omit<ChakraPagination.RootProps, 'type'> {
  size?: ButtonProps['size'];
  variant?: PaginationVariant;
  getHref?: (page: number) => string;
}

const variantMap: Record<PaginationVariant, ButtonVariantMap> = {
  outline: { default: 'ghost', ellipsis: 'plain', current: 'outline' },
  solid: { default: 'plain', ellipsis: 'plain', current: 'solid' },
  subtle: { default: 'ghost', ellipsis: 'plain', current: 'subtle' },
};

export const PaginationRoot = React.forwardRef<HTMLDivElement, PaginationRootProps>(
  function PaginationRoot(props, ref) {
    const { size = 'sm', variant = 'outline', getHref, ...rest } = props;
    return (
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      <RootPropsProvider value={{ size, variantMap: variantMap[variant], getHref }}>
        <ChakraPagination.Root ref={ref} type={getHref ? 'link' : 'button'} {...rest} />
      </RootPropsProvider>
    );
  },
);

export const PaginationEllipsis = React.forwardRef<HTMLDivElement, ChakraPagination.EllipsisProps>(
  function PaginationEllipsis(props, ref) {
    const { size, variantMap } = useRootProps();
    return (
      <ChakraPagination.Ellipsis ref={ref} {...props} asChild>
        <Button as="span" variant={variantMap.ellipsis} size={size} minW="7" px="1">
          <LuEllipsis />
        </Button>
      </ChakraPagination.Ellipsis>
    );
  },
);

export const PaginationItem = React.forwardRef<HTMLButtonElement, ChakraPagination.ItemProps>(
  function PaginationItem(props, ref) {
    const { page } = usePaginationContext();
    const { size, variantMap, getHref } = useRootProps();

    const current = page === props.value;
    const variant = current ? variantMap.current : variantMap.default;

    if (getHref) {
      return (
        <LinkButton href={getHref(props.value)} variant={variant} size={size}>
          {props.value}
        </LinkButton>
      );
    }

    return (
      <ChakraPagination.Item ref={ref} {...props} asChild>
        <Button variant={variant} size={size} minW="7" px="1">
          {props.value}
        </Button>
      </ChakraPagination.Item>
    );
  },
);

export const PaginationFirstTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraPagination.PrevTriggerProps & { prefetch?: (page: number) => void }
>(function PaginationFirstTrigger(props, ref) {
  const { size, variantMap, getHref } = useRootProps();
  const { previousPage, setPage } = usePaginationContext();

  if (getHref && previousPage != null) {
    return (
      <LinkButton
        href={getHref(1)}
        variant={variantMap.default}
        size={size}
        minW="7"
        px="1"
        onPointerEnter={() => previousPage && props.prefetch?.(1)}
      >
        <LuChevronsLeft />
      </LinkButton>
    );
  }

  return (
    <ChakraPagination.PrevTrigger ref={ref} asChild {...props}>
      <IconButton
        variant={variantMap.default}
        size={size}
        onClick={() => setPage(1)}
        onPointerEnter={() => props.prefetch?.(1)}
        minW="7"
        px="1"
      >
        <LuChevronsLeft />
      </IconButton>
    </ChakraPagination.PrevTrigger>
  );
});

export const PaginationLastTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraPagination.PrevTriggerProps & {
    prefetch?: (page: number) => void;
  }
>(function PaginationLastTrigger(props, ref) {
  const { size, variantMap, getHref } = useRootProps();
  const { nextPage, setPage, totalPages } = usePaginationContext();

  if (getHref && nextPage != null) {
    return (
      <LinkButton
        href={getHref(totalPages)}
        variant={variantMap.default}
        size={size}
        minW="7"
        px="1"
        onPointerEnter={() => nextPage && props.prefetch?.(totalPages)}
      >
        <LuChevronsRight />
      </LinkButton>
    );
  }

  return (
    <ChakraPagination.NextTrigger ref={ref} asChild {...props}>
      <IconButton
        variant={variantMap.default}
        size={size}
        onClick={() => setPage(totalPages)}
        minW="7"
        px="1"
        onPointerEnter={() => props.prefetch?.(totalPages)}
      >
        <LuChevronsRight />
      </IconButton>
    </ChakraPagination.NextTrigger>
  );
});

export const PaginationPrevTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraPagination.PrevTriggerProps & {
    prefetch?: (page: number) => void;
  }
>(function PaginationPrevTrigger(props, ref) {
  const { size, variantMap, getHref } = useRootProps();
  const { previousPage } = usePaginationContext();

  if (getHref && previousPage != null) {
    return (
      <LinkButton
        href={getHref(previousPage)}
        variant={variantMap.default}
        size={size}
        minW="7"
        px="1"
        onPointerEnter={() => previousPage && props.prefetch?.(previousPage)}
      >
        <LuChevronLeft />
      </LinkButton>
    );
  }

  return (
    <ChakraPagination.PrevTrigger
      ref={ref}
      asChild
      {...props}
      onPointerEnter={() => previousPage && props.prefetch?.(previousPage)}
    >
      <IconButton variant={variantMap.default} size={size} minW="7" px="1">
        <LuChevronLeft />
      </IconButton>
    </ChakraPagination.PrevTrigger>
  );
});

export const PaginationNextTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraPagination.NextTriggerProps & {
    prefetch?: (page: number) => void;
  }
>(function PaginationNextTrigger(props, ref) {
  const { size, variantMap, getHref } = useRootProps();
  const { nextPage } = usePaginationContext();

  if (getHref && nextPage != null) {
    return (
      <LinkButton
        href={getHref(nextPage)}
        variant={variantMap.default}
        size={size}
        minW="7"
        px="1"
        onPointerEnter={() => nextPage && props.prefetch?.(nextPage)}
      >
        <LuChevronRight />
      </LinkButton>
    );
  }

  return (
    <ChakraPagination.NextTrigger
      ref={ref}
      asChild
      {...props}
      onPointerEnter={() => nextPage && props.prefetch?.(nextPage)}
    >
      <IconButton variant={variantMap.default} size={size} minW="7" px="1">
        <LuChevronRight />
      </IconButton>
    </ChakraPagination.NextTrigger>
  );
});

export function PaginationItems({
  prefetch,
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  prefetch?: (page: number) => void;
}) {
  return (
    <ChakraPagination.Context>
      {({ pages }) =>
        pages.map((page, index) => {
          return page.type === 'ellipsis' ? (
            <PaginationEllipsis key={index} index={index} {...props} />
          ) : (
            <PaginationItem
              key={index}
              type="page"
              value={page.value}
              {...props}
              onPointerEnter={() => prefetch?.(page.value)}
            />
          );
        })
      }
    </ChakraPagination.Context>
  );
}

interface PageTextProps extends TextProps {
  format?: 'short' | 'compact' | 'long';
}

export const PaginationPageText = React.forwardRef<HTMLParagraphElement, PageTextProps>(
  function PaginationPageText(props, ref) {
    const { format = 'compact', ...rest } = props;
    const { page, totalPages, pageRange, count } = usePaginationContext();
    const content = React.useMemo(() => {
      if (format === 'short') return `${page} / ${totalPages}`;
      if (format === 'compact') return `${page} sur ${totalPages}`;
      return `${pageRange.start + 1} - ${pageRange.end} sur ${count}`;
    }, [format, page, totalPages, pageRange, count]);

    return (
      <Text fontWeight="bold" ref={ref} {...rest}>
        {content}
      </Text>
    );
  },
);

export function PaginationBlock({
  total,
  page,
  setPage,
  prefetch,
  pageSize = 10,
  buildHref,
}: {
  total?: number;
  page: number;
  setPage?: (page: number) => void;
  prefetch?: (page: number) => void;
  pageSize?: number;
  buildHref?: (page: number) => string;
}) {
  const siblingCount = useBreakpointValue({ base: 0, md: 1 });
  return (
    <PaginationRoot
      count={total ?? 0}
      pageSize={pageSize}
      page={page}
      onPageChange={setPage ? (e) => setPage(e.page) : undefined}
      variant="subtle"
      getHref={buildHref}
      siblingCount={siblingCount}
    >
      <HStack>
        <PaginationFirstTrigger prefetch={prefetch} hideBelow="sm" />
        <PaginationPrevTrigger prefetch={prefetch} />
        <PaginationItems prefetch={prefetch} />
        <PaginationNextTrigger prefetch={prefetch} />
        <PaginationLastTrigger prefetch={prefetch} hideBelow="lg" />
      </HStack>
    </PaginationRoot>
  );
}
